import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, DollarSign, ShoppingCart, Users, Gift, MessageSquare, Info } from "lucide-react";
import { CampaignPromoPerformance } from "@/hooks/sms/types";
import { RevenueTooltip } from "@/components/ui/revenue-tooltip";
import { useCampaignRevenueBreakdown } from "@/hooks/sms/useCampaignRevenueBreakdown";

interface CampaignPromoPerformanceProps {
  performance: CampaignPromoPerformance;
  totalRecipients: number;
  totalOptOuts: number;
  campaignId?: string;
  isLoading?: boolean;
  error?: string | null;
}

export const CampaignPromoPerformanceComponent: React.FC<CampaignPromoPerformanceProps> = ({
  performance,
  totalRecipients,
  totalOptOuts,
  campaignId,
  isLoading,
  error
}) => {
  // Fetch detailed revenue breakdown for tooltips
  const { data: breakdown } = useCampaignRevenueBreakdown(campaignId);
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Promo Code Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading performance data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Promo Code Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-600">
            Error loading performance data: {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate derived metrics
  const conversionRate = totalRecipients > 0 ? (performance.orders_count / totalRecipients) * 100 : 0;
  const averageOrderValue = performance.orders_count > 0 ? performance.gross_revenue / performance.orders_count : 0;
  const valueProvided = performance.orders_count * (performance.promo_code_id ? 0 : 0); // This would need promo code details

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Promo Code Performance
        </CardTitle>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="outline">{performance.promo_code}</Badge>
          <span>
            {formatDate(performance.attribution_start_date)} - {formatDate(performance.attribution_end_date)}
          </span>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Revenue Metrics */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Revenue Metrics
            </h4>
            
            <div className="space-y-3">
              {breakdown ? (
                <RevenueTooltip
                  title="Gross Revenue Calculation"
                  calculation={
                    breakdown.promo_discount_type === 'free_item' 
                      ? [
                          { label: "Cart Value", value: breakdown.total_cart_value },
                          { label: "Delivery Fees Paid by Customer", value: breakdown.total_delivery_fees_paid_by_customer, isAddition: true },
                          { label: "Gross Revenue", value: breakdown.gross_revenue, isTotal: true }
                        ]
                      : [
                          { label: "Cart Value", value: breakdown.total_cart_value },
                          { label: "Discount Value", value: breakdown.total_discount_value, isSubtraction: true },
                          { label: "Delivery Fees Paid by Customer", value: breakdown.total_delivery_fees_paid_by_customer, isAddition: true },
                          { label: "Gross Revenue", value: breakdown.gross_revenue, isTotal: true }
                        ]
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Gross Revenue</span>
                      <Info className="h-3 w-3 text-muted-foreground hover:text-foreground transition-colors" />
                    </div>
                    <span className="font-semibold">{formatCurrency(performance.gross_revenue)}</span>
                  </div>
                </RevenueTooltip>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Gross Revenue</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(performance.gross_revenue)}</span>
                </div>
              )}
              
              {breakdown ? (
                <RevenueTooltip
                  title="Net Revenue Calculation"
                  calculation={[
                    { label: "Gross Revenue", value: breakdown.gross_revenue },
                    { label: "Delivery Fees Paid by Restaurant", value: breakdown.total_delivery_fees_paid_by_restaurant, isSubtraction: true },
                    { label: "Stripe Fees", value: breakdown.total_stripe_fees, isSubtraction: true },
                    { label: "Commission Fees", value: breakdown.total_commission_fees, isSubtraction: true },
                    { label: "SMS Costs", value: breakdown.sms_cost, isSubtraction: true },
                    { label: "Net Revenue", value: breakdown.net_revenue, isTotal: true }
                  ]}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                      <span className="text-sm">Net Revenue</span>
                      <Info className="h-3 w-3 text-muted-foreground hover:text-foreground transition-colors" />
                    </div>
                    <span className="font-semibold">{formatCurrency(performance.net_revenue)}</span>
                  </div>
                </RevenueTooltip>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">Net Revenue</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(performance.net_revenue)}</span>
                </div>
              )}
              
              {breakdown ? (
                <RevenueTooltip
                  title="Net Profit Calculation"
                  calculation={
                    breakdown.promo_discount_type === 'free_item'
                      ? [
                          { label: "Net Revenue", value: breakdown.net_revenue },
                          { label: `COGS on items sold (${breakdown.cogs_percentage}%)`, value: breakdown.total_cogs, isSubtraction: true },
                          { label: "Free Item Costs", value: breakdown.total_free_item_costs, isSubtraction: true },
                          { label: "Net Profit", value: breakdown.net_profit, isTotal: true }
                        ]
                      : [
                          { label: "Net Revenue", value: breakdown.net_revenue },
                          { label: `COGS on items sold (${breakdown.cogs_percentage}%)`, value: breakdown.total_cogs, isSubtraction: true },
                          { label: "Net Profit", value: breakdown.net_profit, isTotal: true }
                        ]
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                      <span className="text-sm">Net Profit</span>
                      <Info className="h-3 w-3 text-muted-foreground hover:text-foreground transition-colors" />
                    </div>
                    <span className="font-semibold">{formatCurrency(performance.net_profit)}</span>
                  </div>
                </RevenueTooltip>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-purple-600" />
                    <span className="text-sm">Net Profit</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(performance.net_profit)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Order Metrics */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Order Metrics
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">Orders</span>
                </div>
                <span className="font-semibold">{performance.orders_count}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm">Conversion Rate</span>
                </div>
                <span className="font-semibold">{formatPercentage(conversionRate)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-teal-600" />
                  <span className="text-sm">Avg Order Value</span>
                </div>
                <span className="font-semibold">{formatCurrency(averageOrderValue)}</span>
              </div>
            </div>
          </div>

          {/* Cost & Value Metrics */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Cost & Value
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-pink-600" />
                  <span className="text-sm">SMS Cost</span>
                </div>
                <span className="font-semibold">{formatCurrency(performance.sms_cost)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gift className="h-4 w-4 text-red-600" />
                  <span className="text-sm">
                    {breakdown?.promo_discount_type === 'free_item' ? 'Value Provided' : 'Discount Value'}
                  </span>
                </div>
                <span className="font-semibold">{formatCurrency(performance.total_discount_value)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-600" />
                  <span className="text-sm">Recipients</span>
                </div>
                <span className="font-semibold">{totalRecipients.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-6 pt-6 border-t">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(performance.gross_revenue)}
              </div>
              <div className="text-xs text-muted-foreground">Total Revenue</div>
            </div>
            
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {performance.orders_count}
              </div>
              <div className="text-xs text-muted-foreground">Orders</div>
            </div>
            
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {formatPercentage(conversionRate)}
              </div>
              <div className="text-xs text-muted-foreground">Conversion</div>
            </div>
            
            <div>
              <div className="text-2xl font-bold text-orange-600">
                {formatCurrency(averageOrderValue)}
              </div>
              <div className="text-xs text-muted-foreground">Avg Order</div>
            </div>

            <div>
              <div className="text-2xl font-bold text-red-600">
                {totalOptOuts}
              </div>
              <div className="text-xs text-muted-foreground">Opt-outs</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};